/**
 * Feuille « Publier / Programmer » — Lot 2 (2026-09-03).
 * Un seul composant, ouvert depuis les idées de contenu, le studio, la
 * bibliothèque ou le calendrier. Règles :
 *  - mention IA activée par défaut sur le généré (AI Act art. 50) ;
 *  - options TikTok conformes (confidentialité sans valeur par défaut,
 *    interactions désactivées par défaut, consentement + aperçu) ;
 *  - jamais de valeur devinée : tout ce qui est envoyé vient du membre.
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarClock, Check, ImageIcon, Loader2, Sparkles, Upload, X } from 'lucide-react';
import { ApiError, apiRequest } from '@/lib/api';
import { useVisualLibrary } from '@/hooks/useVisuals';
import {
  PLATFORM_LABEL_KEY,
  PLATFORM_TINT,
  useAccounts,
  usePublish,
  useTiktokOptions,
  useUploadMedia,
  type Platform,
  type PublishBody,
  type StoredMedia,
} from '@/hooks/usePublishing';

const CAPTION_LIMIT: Record<Platform, number> = { instagram: 2200, tiktok: 2200, linkedin: 3000, facebook: 63206 };
/**
 * Instagram n'accepte, pour un post image, qu'un ratio entre 4:5 (0,8) et
 * 1,91:1. Un visuel au format story (9:16 ≈ 0,56) est refusé par Meta : on le
 * détecte AVANT l'envoi plutôt que de laisser le membre découvrir l'échec.
 */
const IG_RATIO_MIN = 0.8;
const IG_RATIO_MAX = 1.91;
const igRatioOk = (m: StoredMedia | null): boolean => {
  if (!m || !m.width || !m.height) return true;
  const r = m.width / m.height;
  return r >= IG_RATIO_MIN && r <= IG_RATIO_MAX;
};
const PRIVACY_KEY: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'publish.privacyPublic',
  MUTUAL_FOLLOW_FRIENDS: 'publish.privacyFriends',
  FOLLOWER_OF_CREATOR: 'publish.privacyFollowers',
  SELF_ONLY: 'publish.privacySelf',
};

export interface PublishSheetProps {
  open: boolean;
  onClose: () => void;
  origin: PublishBody['origin'];
  originRef?: string;
  initialCaption?: string;
  /** Visuel de la bibliothèque à publier (chargé puis envoyé en média). */
  initialVisualId?: string;
  /** Média déjà stocké (ex. republication depuis le calendrier). */
  initialMedia?: StoredMedia;
}

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function PublishSheet(props: PublishSheetProps) {
  const { open, onClose, origin, originRef, initialCaption = '', initialVisualId, initialMedia } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accounts = useAccounts(open);
  const library = useVisualLibrary();
  const upload = useUploadMedia();
  const publish = usePublish();

  const [caption, setCaption] = useState(initialCaption);
  const [media, setMedia] = useState<StoredMedia | null>(initialMedia ?? null);
  const [pickingLibrary, setPickingLibrary] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [aiGenerated, setAiGenerated] = useState(origin !== 'free');
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [dateValue, setDateValue] = useState(() => toLocalInputValue(new Date(Date.now() + 60 * 60_000)));
  const [tiktok, setTiktok] = useState({ privacy: '', comment: false, duet: false, stitch: false, preview: false, draft: false });
  const [capacityHit, setCapacityHit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<'now' | 'later' | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Réinitialisation à chaque ouverture.
  useEffect(() => {
    if (!open) return;
    setCaption(initialCaption);
    setMedia(initialMedia ?? null);
    setSelected([]);
    setAiGenerated(origin !== 'free');
    setWhen('now');
    setTiktok({ privacy: '', comment: false, duet: false, stitch: false, preview: false, draft: false });
    setCapacityHit(false);
    setError(null);
    setDone(null);
    setPickingLibrary(false);
  }, [open, initialCaption, initialMedia, origin]);

  // Visuel initial → média (une seule fois par ouverture).
  useEffect(() => {
    if (!open || !initialVisualId || media) return;
    void pickVisual(initialVisualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialVisualId]);

  const active = useMemo(() => (accounts.data?.accounts ?? []).filter((a) => a.status !== 'disconnected'), [accounts.data]);
  const selectedAccounts = active.filter((a) => selected.includes(a.id));
  const tiktokAccount = selectedAccounts.find((a) => a.platform === 'tiktok') ?? null;
  const tiktokOptions = useTiktokOptions(tiktokAccount?.id ?? null);
  const captionLimit = selectedAccounts.length ? Math.min(...selectedAccounts.map((a) => CAPTION_LIMIT[a.platform])) : 2200;

  async function pickVisual(id: string): Promise<void> {
    setError(null);
    try {
      const full = await apiRequest<{ export_url: string | null }>(`/visuals/${id}`);
      if (!full.export_url) throw new Error('no_export');
      const stored = await upload.mutateAsync({ data_url: full.export_url, source: 'visual' });
      setMedia(stored);
      setPickingLibrary(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('publish.error'));
    }
  }

  function onFile(e: ChangeEvent<HTMLInputElement>): void {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const stored = await upload.mutateAsync({ data_url: String(reader.result), source: 'upload' });
        setMedia(stored);
        if (origin === 'free') setAiGenerated(false);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('publish.error'));
      }
    };
    reader.readAsDataURL(f);
    e.target.value = '';
  }

  function toggle(id: string, platform: Platform): void {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // un seul compte par réseau
      const others = prev.filter((x) => active.find((a) => a.id === x)?.platform !== platform);
      return [...others, id];
    });
  }

  const igSelected = selectedAccounts.some((a) => a.platform === 'instagram');
  const ratioBlocked = igSelected && !igRatioOk(media);
  const tiktokValid = !tiktokAccount || (tiktok.privacy !== '' && tiktok.preview);
  const canSubmit = !!media && caption.trim().length > 0 && caption.length <= captionLimit && selected.length > 0 && tiktokValid && !ratioBlocked && !publish.isPending && !upload.isPending;

  async function submit(): Promise<void> {
    if (!media || !canSubmit) return;
    setError(null);
    const body: PublishBody = {
      caption: caption.trim(),
      media_key: media.key,
      target_account_ids: selected,
      ai_generated: aiGenerated,
      origin,
      origin_ref: originRef,
    };
    if (when === 'later') body.scheduled_for = new Date(dateValue).toISOString();
    if (tiktokAccount) {
      body.tiktok = {
        privacy_level: tiktok.privacy as NonNullable<PublishBody['tiktok']>['privacy_level'],
        allow_comment: tiktok.comment,
        allow_duet: tiktok.duet,
        allow_stitch: tiktok.stitch,
        draft: tiktok.draft || undefined,
      };
    }
    try {
      await publish.mutateAsync(body);
      setDone(when);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'tiktok_capacity') {
        setCapacityHit(true);
        setError(t('publish.tiktokCapacity'));
      } else if (e instanceof ApiError && e.code === 'daily_limit_reached') {
        setError(t('publish.limit'));
      } else {
        setError(e instanceof ApiError ? e.message : t('publish.error'));
      }
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex justify-end bg-night/40 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label={t('publish.title')}
            onClick={(e) => e.stopPropagation()}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="flex h-full w-full max-w-[520px] flex-col overflow-hidden bg-paper shadow-2xl sm:rounded-l-[28px]"
            style={{ backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #FFFAF0 60%, #F8F6F0 100%)' }}
          >
            <header className="flex items-center justify-between border-b border-ink/[0.06] px-5 py-4">
              <h2 className="font-display text-[18px] font-800 tracking-[-0.015em] text-ink">{t('publish.title')}</h2>
              <button type="button" onClick={onClose} className="rounded-full p-2 text-ink/50 transition hover:bg-ink/[0.05] hover:text-ink" aria-label={t('common.close')}><X size={18} /></button>
            </header>

            {done ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-pink/10 text-pink"><Check size={26} /></span>
                <p className="text-[15px] font-700 text-ink">{done === 'later' ? t('publish.doneScheduled') : t('publish.done')}</p>
                <button type="button" className="btn-primary px-5 py-2.5 text-[13px]" onClick={() => { onClose(); navigate('/calendrier'); }}>{t('publish.openCalendar')}</button>
              </div>
            ) : (
              <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
                {/* Image */}
                <section>
                  <p className="kicker mb-2 text-violet"><ImageIcon size={11} /> {t('publish.image')}</p>
                  {media ? (
                    <div className="relative overflow-hidden rounded-2xl border border-ink/[0.06] bg-ink/[0.03]">
                      <img src={media.url} alt="" className="max-h-[260px] w-full object-contain" />
                      <button type="button" onClick={() => setMedia(null)} className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-ink/60 hover:text-pink" aria-label={t('common.delete')}><X size={14} /></button>
                    </div>
                  ) : upload.isPending ? (
                    <p className="flex items-center gap-2 text-[13px] text-ink/60"><Loader2 size={14} className="animate-spin" /> {t('publish.uploading')}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => setPickingLibrary((v) => !v)} className="btn-ghost px-3 py-2 text-[12.5px]">{t('publish.pickFromLibrary')}</button>
                      <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px]"><Upload size={13} /> {t('publish.upload')}</button>
                      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
                    </div>
                  )}
                  {pickingLibrary && !media && (
                    <div className="mt-3 grid max-h-[220px] grid-cols-3 gap-2 overflow-y-auto rounded-2xl border border-ink/[0.06] p-2">
                      {(library.data ?? []).map((v) => {
                        const meta = (v.layers ?? []).find((l) => l && l.__meta);
                        return (
                          <button key={v.id} type="button" onClick={() => void pickVisual(v.id)} className="aspect-square overflow-hidden rounded-xl bg-ink/[0.04] transition hover:ring-2 hover:ring-pink/60" title={meta?.title ?? ''}>
                            {meta?.thumbnail ? <img src={meta.thumbnail} alt={meta.title ?? ''} className="h-full w-full object-cover" /> : <ImageIcon className="m-auto text-ink/30" />}
                          </button>
                        );
                      })}
                      {library.data && library.data.length === 0 && <p className="col-span-3 p-3 text-center text-[12px] text-ink/50">—</p>}
                    </div>
                  )}
                  {!media && !upload.isPending && <p className="mt-2 text-[12px] text-ink/45">{t('publish.noImage')}</p>}
                </section>

                {/* Légende */}
                <section>
                  <p className="kicker mb-2 text-pink"><Sparkles size={11} /> {t('publish.caption')}</p>
                  <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={6} className="w-full resize-y rounded-2xl border border-ink/[0.08] bg-white/70 px-3.5 py-3 text-[13.5px] leading-[1.5] text-ink outline-none focus:border-pink/60 focus:ring-2 focus:ring-pink/20" />
                  <p className={`mt-1 text-[11px] ${caption.length > captionLimit ? 'text-pink-deep' : 'text-ink/45'}`}>{caption.length} / {t('publish.captionHelp', { count: captionLimit })}</p>
                  <label className="mt-2 flex cursor-pointer items-start gap-2.5">
                    <input type="checkbox" checked={aiGenerated} onChange={(e) => setAiGenerated(e.target.checked)} className="mt-0.5 accent-pink" />
                    <span><span className="text-[13px] font-700 text-ink">{t('publish.aiToggle')}</span><br /><span className="text-[11.5px] text-ink/55">{t('publish.aiHelp')}</span></span>
                  </label>
                </section>

                {/* Cibles */}
                <section>
                  <p className="kicker mb-2 text-blue">{t('publish.targets')}</p>
                  {accounts.isLoading ? (
                    <Loader2 size={16} className="animate-spin text-ink/40" />
                  ) : active.length === 0 ? (
                    <p className="text-[13px] text-ink/60">{t('publish.noAccounts')} <button type="button" className="font-700 text-pink hover:underline" onClick={() => { onClose(); navigate('/comptes'); }}>{t('publish.connectAccounts')}</button></p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {active.map((a) => {
                        const on = selected.includes(a.id);
                        const blocked = a.status === 'needs_reconnect';
                        return (
                          <button key={a.id} type="button" disabled={blocked} onClick={() => toggle(a.id, a.platform)} className={`flex items-center gap-2.5 rounded-2xl border px-3 py-2.5 text-left transition ${on ? 'border-pink bg-pink/5' : 'border-ink/[0.08] bg-white/60 hover:border-ink/20'} disabled:opacity-50`}>
                            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: PLATFORM_TINT[a.platform] }} />
                            <span className="min-w-0 flex-1">
                              <span className="block text-[12.5px] font-700 text-ink">{t(PLATFORM_LABEL_KEY[a.platform])}</span>
                              <span className="block truncate text-[11.5px] text-ink/55">{blocked ? t('publish.reconnect') : a.username ?? a.display_name ?? ''}</span>
                            </span>
                            {on && <Check size={14} className="text-pink" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* Quand */}
                <section>
                  <p className="kicker mb-2 text-violet"><CalendarClock size={11} /> {t('publish.when')}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => setWhen('now')} className={`rounded-full px-3 py-1.5 text-[12.5px] font-700 ${when === 'now' ? 'bg-ink text-white' : 'bg-ink/[0.05] text-ink/70'}`}>{t('publish.now')}</button>
                    <button type="button" onClick={() => setWhen('later')} className={`rounded-full px-3 py-1.5 text-[12.5px] font-700 ${when === 'later' ? 'bg-ink text-white' : 'bg-ink/[0.05] text-ink/70'}`}>{t('publish.later')}</button>
                    {when === 'later' && (
                      <input type="datetime-local" value={dateValue} min={toLocalInputValue(new Date(Date.now() + 5 * 60_000))} onChange={(e) => setDateValue(e.target.value)} className="rounded-xl border border-ink/[0.08] bg-white/70 px-3 py-1.5 text-[12.5px] text-ink" />
                    )}
                  </div>
                  {when === 'later' && <p className="mt-1 text-[11px] text-ink/45">{t('publish.timezone')}</p>}
                </section>

                {/* TikTok */}
                {tiktokAccount && (
                  <section className="rounded-2xl border border-ink/[0.08] bg-white/60 p-3.5">
                    <p className="kicker mb-2 text-ink/70">{t('publish.tiktokTitle')}</p>
                    <label className="block text-[12px] font-700 text-ink">{t('publish.tiktokPrivacy')}
                      <select value={tiktok.privacy} onChange={(e) => setTiktok((s) => ({ ...s, privacy: e.target.value }))} className="mt-1 w-full rounded-xl border border-ink/[0.08] bg-white px-3 py-2 text-[12.5px] font-500 text-ink">
                        <option value="">{t('publish.tiktokPrivacyPlaceholder')}</option>
                        {(tiktokOptions.data?.privacyLevels ?? []).map((lvl) => <option key={lvl} value={lvl}>{PRIVACY_KEY[lvl] ? t(PRIVACY_KEY[lvl]!) : lvl}</option>)}
                      </select>
                    </label>
                    <div className="mt-2 grid grid-cols-1 gap-1.5 text-[12.5px] text-ink/80">
                      <label className="flex items-center gap-2"><input type="checkbox" className="accent-pink" checked={tiktok.comment} onChange={(e) => setTiktok((s) => ({ ...s, comment: e.target.checked }))} /> {t('publish.tiktokAllowComment')}</label>
                      <label className="flex items-center gap-2"><input type="checkbox" className="accent-pink" checked={tiktok.duet} onChange={(e) => setTiktok((s) => ({ ...s, duet: e.target.checked }))} /> {t('publish.tiktokAllowDuet')}</label>
                      <label className="flex items-center gap-2"><input type="checkbox" className="accent-pink" checked={tiktok.stitch} onChange={(e) => setTiktok((s) => ({ ...s, stitch: e.target.checked }))} /> {t('publish.tiktokAllowStitch')}</label>
                      <label className="mt-1 flex items-start gap-2 font-700"><input type="checkbox" className="mt-0.5 accent-pink" checked={tiktok.preview} onChange={(e) => setTiktok((s) => ({ ...s, preview: e.target.checked }))} /> {t('publish.tiktokPreview')}</label>
                      {capacityHit && <label className="flex items-center gap-2 font-700 text-pink"><input type="checkbox" className="accent-pink" checked={tiktok.draft} onChange={(e) => setTiktok((s) => ({ ...s, draft: e.target.checked }))} /> {t('publish.tiktokDraft')}</label>}
                    </div>
                    <p className="mt-2 text-[11px] text-ink/50">{t('publish.tiktokConsent')}</p>
                  </section>
                )}

                {ratioBlocked && (
                  <p role="alert" className="rounded-xl bg-amber-500/10 px-3.5 py-2.5 text-[13px] font-600 text-amber-800">
                    {t('publish.ratioWarning')}
                  </p>
                )}

                {error && <p role="alert" className="rounded-xl bg-pink/10 px-3.5 py-2.5 text-[13px] font-600 text-pink-deep">{error}</p>}
              </div>
            )}

            {!done && (
              <footer className="border-t border-ink/[0.06] px-5 py-4">
                <button type="button" disabled={!canSubmit} onClick={() => void submit()} className="btn-primary w-full justify-center py-3 text-[14px] disabled:opacity-50">
                  {publish.isPending ? <Loader2 size={16} className="animate-spin" /> : when === 'later' ? t('publish.submitLater') : t('publish.submitNow')}
                </button>
              </footer>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
